import React, { useEffect, useState } from 'react';
import Select, { components, Props as SelectProps } from 'react-select';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { select2Config } from '../config';
import './SelectTwo.css';

interface SelectTwoProps {
    name?: string;
    id?: string;
    onFocus?: (value: any) => void;
    inputValue?: any;
    options: SelectTwoOption[];
    onChange?: (value: any) => void;
    placeholder?: string;
    hasError?: boolean;
    isMulti?: boolean;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    isSortable?: boolean;
    isClearable?: boolean;
    label?: string;
    helpText?: string;
    className?: string;
}

interface SelectTwoOption {
    value: string | number | null;
    label: string;
}

export const SelectTwoMapperOptions = (
    options: any[],
    valueName = 'id',
    labelNames = ['name'],
    LabeLSeparation = ' | '
) => {
    if (!options) return [];

    return options.map((option: any) => {
        const labels: string[] = [];
        labelNames.map((labelName: string) => {
            labels.push(option[labelName]);
        });
        return {
            value: option[valueName],
            label: labels.join(LabeLSeparation)
        };
    });
};

const SortableMultiValueLabel = SortableHandle((props: any) => (
    <components.MultiValueLabel {...props} />
));

const SortableMultiValue = SortableElement((props: any) => (
    <components.MultiValue
        {...props}
        innerProps={{
            ...props.innerProps,
            onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
            }
        }}
    />
));

const SortableSelect = SortableContainer(Select);
const selectedOption = (options: SelectTwoOption[], value: string | number | null) => {
    return options.find((option: SelectTwoOption) => option.value == value) || null;
};

const getCustomStyles = (hasError: boolean, size = 'medium') => {
    // Definir tamaños basados en el prop size
    const sizeConfig = {
        small: {
            height: '2.25rem',
            padding: '0.375rem 0.75rem 0.375rem 1rem',
            fontSize: '0.875rem',
            placeholderTop: '0.75rem',
            placeholderLeft: '0.75rem',
            placeholderFontSize: '0.875rem',
            optionPadding: '0.5rem 0.75rem',
            optionFontSize: '0.8rem'
        },
        medium: {
            height: '3rem', 
            padding: '0.5rem 0.875rem 0.5rem 1.125rem',
            fontSize: '0.9rem',
            placeholderTop: '1.5rem',
            placeholderLeft: '1.125rem',
            placeholderFontSize: '0.9rem',
            optionPadding: '0.625rem 1rem',
            optionFontSize: '0.875remrem'
        },
        large: {
            height: '3.5rem',
            padding: '0.625rem 1rem 0.625rem 1.5rem',
            fontSize: '1rem',
            placeholderTop: '1.25rem',
            placeholderLeft: '1.5rem',
            placeholderFontSize: '1rem',
            optionPadding: '0.75rem 1rem',
            optionFontSize: '0.95rem'
        }
    };

    const currentSize = sizeConfig[size as keyof typeof sizeConfig] || sizeConfig.medium;

    return {
        control: (provided: any, state: any) => ({
            ...provided,
            minHeight: currentSize.height,
            height: currentSize.height,
            border: hasError ? '2px solid #ef4444' : '2px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 24, 73, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#e5e7eb'
            }
        }),
        valueContainer: (provided: any, state: any) => ({
            ...provided,
            minHeight: currentSize.height,
            height: currentSize.height,
            padding: currentSize.padding,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.25rem'
        }),
        input: (provided: any) => ({
            ...provided,
            margin: 0,
            padding: '0 0.25rem',
            minHeight: '1.5rem',
            fontSize: currentSize.fontSize
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: '#9ca3af',
            fontSize: currentSize.placeholderFontSize,
            margin: 0,
            position: 'absolute',
            top: currentSize.placeholderTop,
            left: currentSize.placeholderLeft,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 1
        }),
        indicatorsContainer: (provided: any) => ({
            ...provided,
            position: 'absolute',
            right: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            height: '1.5rem',
            display: 'flex',
            alignItems: 'center'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        multiValue: (provided: any) => ({
            ...provided,
            backgroundColor: '#001849',
            borderRadius: '6px',
            margin: 0,
            height: '1.25rem',
            display: 'flex',
            alignItems: 'center'
        }),
        multiValueLabel: (provided: any) => ({
            ...provided,
            color: '#ffffff',
            fontSize: '0.8rem',
            padding: '0.2rem 0.4rem',
            lineHeight: 1
        }),
        multiValueRemove: (provided: any) => ({
            ...provided,
            color: '#ffffff',
            borderRadius: '0 6px 6px 0',
            padding: '0.2rem 0.4rem',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff'
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            borderRadius: '12px',
            border: '2px solid #e5e7eb',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            marginTop: '0.5rem',
            zIndex: 9999
        }),
        menuList: (provided: any) => ({
            ...provided,
            padding: '0.5rem'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            padding: currentSize.optionPadding,
            borderRadius: '8px',
            marginBottom: '0.25rem',
            fontSize: currentSize.optionFontSize,
            color: '#374151',
            backgroundColor: state.isFocused ? '#f3f4f6' : 'transparent',
            '&:hover': {
                backgroundColor: '#f3f4f6',
                color: '#001849'
            },
            ...(state.isSelected && {
                backgroundColor: '#001849',
                color: '#ffffff',
                '&:hover': {
                    backgroundColor: '#001849',
                    color: '#ffffff'
                }
            })
        })
    };
};
const SelectTwo: React.FC<SelectTwoProps> = ({
    name,
    id,
    onFocus,
    inputValue,
    options,
    onChange,
    placeholder = 'Seleccione una opción',
    hasError = false,
    isMulti = false,
    disabled,
    isSortable = false,
    size = 'medium',
    isClearable = false,
    label,
    helpText,
    className = ''
}) => {
    function arrayMove<T>(array: readonly T[], from: number, to: number) {
        const slicedArray = array.slice();
        slicedArray.splice(to < 0 ? array.length + to : to, 0, slicedArray.splice(from, 1)[0]);
        return slicedArray;
    }
    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const newValue = arrayMove(inputValue, oldIndex, newIndex);
        if (typeof onChange === 'function') {
            onChange(newValue);
        }
    };

    const sortableProps: any = {
        useDragHandle: true,
        axis: 'xy',
        onSortEnd: onSortEnd,
        distance: 4
    };

    const sortableComponents = isSortable
        ? {
              MultiValue: SortableMultiValue,
              MultiValueLabel: SortableMultiValueLabel
          }
        : {};

    const hasValue = Array.isArray(inputValue) ? inputValue.length > 0 : inputValue !== null && inputValue !== undefined && inputValue !== '';

    return (
        <div className={`select-two-container ${hasError ? 'error' : ''} ${disabled ? 'disabled' : ''} ${size} ${className}`}>
            {label && (
                <label className="select-two-label floating">
                    {label}
                </label>
            )}
            <SortableSelect
                {...sortableProps}
                id={id}
                isMulti={isMulti}
                options={options}
                value={Array.isArray(inputValue) ? inputValue : selectedOption(options, inputValue)}
                onChange={onChange}
                components={sortableComponents}
                placeholder={placeholder}
                isDisabled={disabled}
                name={name}
                styles={getCustomStyles(hasError, size)}
                isClearable={isClearable}
                onFocus={onFocus}
            />
            {helpText && (
                <small className="select-two-help-text">
                    {helpText}
                </small>
            )}
        </div>
    );
};

export default SelectTwo;
